import { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import Flatpickr from "react-flatpickr";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import Alert from "../Alert";
import { alertContext } from "../contexts/AlertProvider";
import Pagination from "../layout/Pagination";
import Status from "./actions/Status";
import Suspend from "./actions/Suspend";
import refreshDriverApp from "../socket/RefreshDriverApp";
import logoutDriver from "../socket/LogoutDriver";
import Online from "./actions/Online";
import PermissionGate from "../layout/PermissionGate";
import socket from "../socket/socket";

const AllDrivers = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [type, setType] = useState([]);
  const [ringPriorityCategory, setRingPriorityCategory] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [driver, setDriver] = useState({});
  const [popup, setPopup] = useState("");
  const [error, setError] = useState({});
  const [sharing, setSharing] = useState(0);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [alertConfig, setAlertConfig] = useContext(alertContext);
  const dateRangePickerRef = useRef(null);
  const fetchCache = useRef(new Set());
  const token = localStorage.getItem("token");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchDrivers = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT
        }all-drivers?page=${page}&limit=${limit}&search=${search}&type=${type.join(
          ",",
        )}&ringPriorityCategory=${ringPriorityCategory.join(
          ",",
        )}&start=${start}&end=${end}&delivaryCompanyId=${null}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          if (page > 1) {
            if (res.data.data.length === 0) {
              setPage(page - 1);
            } else {
              setData(res.data.data);
              setCount(res.data.count);
              setIsLoading(false);
            }
          } else {
            setData(res.data.data);
            setCount(res.data.count);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
        setSelectedDrivers([]);
      })
      .catch(console.error);
  };
  const toggleSelectDriver = (driverId) => {
    setSelectedDrivers((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId]
    );
  };
  const toggleSelectAllDrivers = () => {
    if (selectedDrivers.length === data.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(data.map((item) => item._id));
    }
  };
  const changeDriverRingPriorityBatch = (excludeFromRingPriority) => {
    if (selectedDrivers.length === 0) return;
    axios
      .post(
        `${process.env.REACT_APP_API_ENDPOINT}change-driver-ring-priority-batch`,
        {
          ids: selectedDrivers,
          excludeFromRingPriority: excludeFromRingPriority,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          setSelectedDrivers([]);
          fetchDrivers();
        }
      })
      .catch(console.error);
  };
  const changeDriverLeastPriority = () => {
    if (driver._id === undefined) return;
    const leastRingPriority = driver.leastRingPriority === true ? false : true;
    axios
      .post(
        `${process.env.REACT_APP_API_ENDPOINT}change-driver-least-ring-priority`,
        {
          id: driver._id,
          leastRingPriority: leastRingPriority,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          setDriver({});
          fetchDrivers();
        }
      })
      .catch(console.error);
  };
  const changeDriverLeastRingPriorityBatch = (leastRingPriority) => {
    if (selectedDrivers.length === 0) return;
    axios
      .post(
        `${process.env.REACT_APP_API_ENDPOINT}change-driver-least-ring-priority-batch`,
        {
          ids: selectedDrivers,
          leastRingPriority: leastRingPriority,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          setSelectedDrivers([]);
          fetchDrivers();
        }
      })
      .catch(console.error);
  };
  const fetchSingleDriver = async (driverId) => {
    if (fetchCache.current.has(driverId)) return;
    fetchCache.current.add(driverId);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}driver-data/${driverId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data?.data) {
        const fullDriver = res.data.data;

        setData((prev) => {
          if (!prev) return prev;

          const exists = prev.some((d) => d._id === driverId);

          if (exists) {
            return prev.map((d) => (d._id === driverId ? fullDriver : d));
          }
          return prev;
        });
      }
    } catch (err) {
      console.error("Single Driver Fetch Failed:", err);
    } finally {
      setTimeout(() => fetchCache.current.delete(driverId), 3000);
    }
  };
  const processDriverType = (driverType) => {
    if (type.includes(driverType)) {
      const prevType = [...type];
      const updatedType = [];
      prevType.map((item) => {
        if (item !== driverType) {
          updatedType.push(item);
        }
      });
      setType(updatedType);
    } else {
      const prevType = [...type];
      prevType.push(driverType);
      setType(prevType);
    }
  };
  const processRingPriorityCategory = (category) => {
    if (ringPriorityCategory.includes(category)) {
      setRingPriorityCategory(
        ringPriorityCategory.filter((item) => item !== category),
      );
    } else {
      setRingPriorityCategory([...ringPriorityCategory, category]);
    }
  };
  const reset = () => {
    setPage(1);
    setSearch("");
    setType([]);
    setRingPriorityCategory([]);
    setStart("");
    setEnd("");
  };
  const openCalendar = () => {
    if (dateRangePickerRef.current) {
      dateRangePickerRef.current.flatpickr.open();
    }
  };
  const openPopup = (action, data) => {
    setDriver(data);
    setPopup(action);
  };
  const changeStatus = () => {
    if (driver.id !== undefined && driver.status !== undefined) {
      const status = driver.status === true ? false : true;
      axios
        .post(
          `${process.env.REACT_APP_API_ENDPOINT}change-driver-status`,
          {
            id: driver.id,
            status: status,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            fetchDrivers();
            logoutDriver(driver?.id);
          }
        })
        .catch(console.error);
    }
  };
  const changeAvailability = () => {
    if (driver.id !== undefined && driver?.busy !== undefined) {
      axios
        .patch(
          `${process.env.REACT_APP_API_ENDPOINT}change-driver-availability/${driver?.id}`,
          driver?.busy === true
            ? {
              busy: false,
            }
            : {
              busy: true,
            },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          setPopup("");
          setDriver({});
          if (res.data.code === 1) {
            fetchDrivers();
            refreshDriverApp(driver?.id);
          }
        })
        .catch(console.error);
    }
  };
  const resetPassword = () => {
    axios
      .post(
        process.env.REACT_APP_API_ENDPOINT + "reset-password-driver",
        driver,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            setError({});
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchDrivers();
          }
        }
      })
      .catch(console.error);
  };
  const changePassword = () => {
    const errors = {};
    if (!driver.password?.trim()) {
      errors["password"] = "Please Enter New Password";
    }
    if (!driver.confirmPassword?.trim()) {
      errors["confirmPassword"] = "Please Confirm Password";
    }
    if (driver.password?.trim() !== driver.confirmPassword?.trim()) {
      errors["password"] = "Passwords did not match";
    }

    setError({ ...errors });
    if (errors.password === undefined && errors.confirmPassword === undefined) {
      axios
        .patch(
          `${process.env.REACT_APP_API_ENDPOINT}change-driver-password/${driver?._id}`,
          {
            password: driver?.password,
            confirmPassword: driver?.confirmPassword,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setDriver({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
            }
          }
        })
        .catch(console.error);
    }
  };
  const deleteDriver = () => {
    if (driver.id !== undefined) {
      axios
        .post(
          `${process.env.REACT_APP_API_ENDPOINT}delete-driver`,
          {
            id: driver.id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res.data.code === 1) {
            setPopup("");
            setDriver({});
            fetchDrivers();
            logoutDriver(driver?.id);
          }
        })
        .catch(console.error);
    }
  };
  const fetchDriverSharing = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}settings/get?key=driver-sharing`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.code === 1) {
        const value = Number(res?.data?.data?.value ?? 0);
        setSharing(value);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [page, search, type, ringPriorityCategory, start, end]);
  useEffect(() => {
    fetchDriverSharing();
  }, []);
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      if (!data) return;
      if (data?.driverId) fetchSingleDriver(data.driverId);
    };

    socket.on("admin:ordersUpdate", handleOrderUpdate);
    return () => socket.off("admin:ordersUpdate", handleOrderUpdate);
  }, []);

  return isLoading === true ? (
    <Loader />
  ) : (
    <div className="d-flex flex-column flex-root">
      <Alert />
      {/*begin::Page*/}
      <div className="page d-flex flex-row flex-column-fluid">
        {/*begin::Wrapper*/}
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
              {/*begin::Container*/}
              <div
                id="kt_toolbar_container"
                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
              >
                {/*begin::Page title*/}
                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                  {/*begin::Title*/}
                  <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                    Driver List
                  </h1>
                  {/*end::Title*/}
                </div>
                {/*end::Page title*/}
                {/*begin::Actions*/}
                <PermissionGate required={`view_driver_list`} fallback={<></>}>
                  <div className="d-flex align-items-center overflow-auto">
                    {/*begin::Button*/}
                    <button
                      onClick={() => {
                        processDriverType("Ahmadi Core Support Driver");
                      }}
                      className={
                        type.includes("Ahmadi Core Support Driver")
                          ? "btn bg-primary text-white fw-bold border border-primary me-3"
                          : "btn bg-light-primary text-primary fw-bold border border-primary me-3"
                      }
                    >
                      Core
                    </button>
                    <br />
                    {/*end::Button*/}

                    {/*begin::Button*/}
                    <button
                      onClick={() => {
                        processDriverType("Freelance");
                      }}
                      className={
                        type.includes("Freelance")
                          ? "btn bg-success text-white fw-bold border border-success me-3"
                          : "btn bg-light-success text-success fw-bold border border-success me-3"
                      }
                    >
                      Freelance
                    </button>
                    <br />
                    {/*end::Button*/}

                    {/*begin::Button*/}
                    <button
                      onClick={() => {
                        processDriverType("Delivery Company Fleet Member");
                      }}
                      className={
                        type.includes("Delivery Company Fleet Member")
                          ? "btn bg-warning text-white fw-bold border border-warning me-3 flex-shrink-0"
                          : "btn bg-light-warning text-warning fw-bold border border-warning me-3 flex-shrink-0"
                      }
                    >
                      Fleet Member
                    </button>

                    <br />
                    {/*end::Button*/}

                    {/*begin::Ring Priority Filters*/}
                    <PermissionGate
                      required={`edit_driver_ring_priority`}
                      fallback={<></>}
                    >
                      <>
                        {/*begin::Button*/}
                        <button
                          onClick={() => {
                            processRingPriorityCategory("company_priority");
                          }}
                          title="Delivery company drivers included in priority ring"
                          className={
                            ringPriorityCategory.includes("company_priority")
                              ? "btn bg-success text-white fw-bold border border-success me-3 flex-shrink-0"
                              : "btn bg-light-success text-success fw-bold border border-success me-3 flex-shrink-0"
                          }
                        >
                          Company Priority
                        </button>
                        <br />
                        {/*end::Button*/}

                        {/*begin::Button*/}
                        <button
                          onClick={() => {
                            processRingPriorityCategory("normal");
                          }}
                          title="Freelancers and manually excluded drivers"
                          className={
                            ringPriorityCategory.includes("normal")
                              ? "btn bg-info text-white fw-bold border border-info me-3 flex-shrink-0"
                              : "btn bg-light-info text-info fw-bold border border-info me-3 flex-shrink-0"
                          }
                        >
                          Freelancer
                        </button>
                        <br />
                        {/*end::Button*/}

                        {/*begin::Button*/}
                        <button
                          onClick={() => {
                            processRingPriorityCategory("least_priority");
                          }}
                          title="Drivers marked as least priority"
                          className={
                            ringPriorityCategory.includes("least_priority")
                              ? "btn bg-danger text-white fw-bold border border-danger me-3 flex-shrink-0"
                              : "btn bg-light-danger text-danger fw-bold border border-danger me-3 flex-shrink-0"
                          }
                        >
                          Least Priority
                        </button>
                        <br />
                        {/*end::Button*/}
                      </>
                    </PermissionGate>
                    {/*end::Ring Priority Filters*/}

                    {/*begin::Daterange*/}
                    <div className="d-flex position-relative flex-shrink-0">
                      <Flatpickr
                        ref={dateRangePickerRef}
                        value={[new Date(start), new Date(end)]}
                        options={{ dateFormat: "d-m-Y", mode: "range" }}
                        onChange={([start, end]) => {
                          if (start !== undefined && end !== undefined) {
                            setStart(moment(start).format("YYYY-MM-DD"));
                            setEnd(moment(end).format("YYYY-MM-DD"));
                          }
                        }}
                        className="form-control form-control-lg"
                        placeholder="Pick date"
                      />
                      <i
                        onClick={openCalendar}
                        className="bi-duotone bi-calendar fs-5 position-absolute"
                        style={{ top: "28%", right: "5%", cursor: "pointer" }}
                      ></i>
                    </div>
                    {/*end::Daterange*/}

                    {/*begin::Button*/}
                    <button
                      onClick={reset}
                      className="btn btn-color-danger bg-body mx-3"
                    >
                      Reset
                    </button>
                    {/*end::Button*/}
                  </div>
                </PermissionGate>
                {/*end::Actions*/}
              </div>
              {/*end::Container*/}
            </div>
            {/*end::Toolbar*/}

            {/*begin::Container*/}
            <div
              id="kt_content_container"
              className="d-flex flex-column-fluid align-items-start container-xxl"
            >
              {/*begin::Post*/}
              <div className="content flex-row-fluid" id="kt_content">
                {/*begin::Card*/}
                <PermissionGate required={`view_driver_list`}>
                  <div className="card">
                    {/*begin::Card header*/}
                    <div className="card-header border-0 pt-6">
                      {/*begin::Search*/}
                      <div className="my-1 w-100">
                        <input
                          type="text"
                          value={search}
                          autoComplete="off"
                          onChange={(e) => {
                            setSearch(e.target.value);
                          }}
                          id="searchDriverInput"
                          className="form-control w-100"
                          placeholder="Search Driver Name, Phone"
                        />
                      </div>
                      {/*end::Search*/}
                    </div>
                    {/*end::Card header*/}

                    {/*begin::Card body*/}
                    <div
                      className="card-body py-4 text-center"
                      id="filterDataWrap"
                    >
                      {/*begin::Table*/}
                      {/*begin::Ring Priority Batch Bar*/}
                      <PermissionGate
                        required={`edit_driver_ring_priority`}
                        fallback={<></>}
                      >
                        {selectedDrivers.length > 0 && (
                          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between gap-2 bg-light-warning rounded p-3 mb-4">
                            <span className="fw-bold">
                              {selectedDrivers.length} Driver
                              {selectedDrivers.length === 1 ? "" : "s"}{" "}
                              Selected
                            </span>
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => {
                                  setPopup("batch-exclude-ring-priority");
                                }}
                              >
                                Exclude From Ring Priority
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  setPopup("batch-include-ring-priority");
                                }}
                              >
                                Include In Ring Priority
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setPopup("batch-mark-least-priority");
                                }}
                              >
                                Mark As Least Priority
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-light"
                                onClick={() => {
                                  setPopup("batch-unmark-least-priority");
                                }}
                              >
                                Unmark Least Priority
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                  setSelectedDrivers([]);
                                }}
                              >
                                Clear Selection
                              </button>
                            </div>
                          </div>
                        )}
                      </PermissionGate>
                      {/*end::Ring Priority Batch Bar*/}
                      {data?.length === 0 ? (
                        <div className="text-center w-100">
                          <img
                            className="w-25 mx-auto py-20"
                            src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                            alt="no-content"
                          />
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table align-middle table-row-dashed fs-6 gy-5">
                            <thead>
                              <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                <PermissionGate
                                  required={`edit_driver_ring_priority`}
                                  fallback={<></>}
                                >
                                  <th className="text-center min-w-50px">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={
                                        data.length > 0 &&
                                        selectedDrivers.length === data.length
                                      }
                                      onChange={toggleSelectAllDrivers}
                                      title="Select All"
                                    />
                                  </th>
                                </PermissionGate>
                                <th className="text-center min-w-100px">
                                  Profile Image
                                </th>
                                <th className="text-center min-w-100px">
                                  Driver Name
                                </th>
                                <th className="text-center min-w-100px">
                                  Sharing
                                </th>
                                <th className="text-center min-w-100px">
                                  Location Updated
                                </th>
                                <th className="text-center min-w-100px">
                                  Phone Number
                                </th>
                                <th className="text-center min-w-100px">
                                  Status
                                </th>
                                <PermissionGate
                                  required={`edit_driver_ring_priority`}
                                  fallback={<></>}
                                >
                                  <th className="text-center min-w-100px">
                                    Ring Priority
                                  </th>
                                </PermissionGate>
                                <PermissionGate
                                  required={`edit_driver_ring_priority`}
                                  fallback={<></>}
                                >
                                  <th className="text-center min-w-100px">
                                    Least Priority
                                  </th>
                                </PermissionGate>
                                <th className="text-center min-w-100px">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="text-gray-600 fw-semibold">
                              {data &&
                                data?.map((item, index) => {
                                  return (
                                    <tr className="text-center" key={index}>
                                      <PermissionGate
                                        required={`edit_driver_ring_priority`}
                                        fallback={<></>}
                                      >
                                        <td>
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedDrivers.includes(
                                              item._id,
                                            )}
                                            onChange={() => {
                                              toggleSelectDriver(item._id);
                                            }}
                                          />
                                        </td>
                                      </PermissionGate>
                                      <td>
                                        {/*begin:: Avatar */}
                                        <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                          <Link
                                            to={`/drivers/driver-details/${item?._id}`}
                                          >
                                            <div className="symbol-label">
                                              <img
                                                src={
                                                  process.env
                                                    .REACT_APP_API_PUBLIC_URL +
                                                  item?.profilePhoto
                                                }
                                                alt={item?.name}
                                                className="w-100"
                                              />
                                            </div>
                                          </Link>
                                        </div>
                                        {/*end::Avatar*/}
                                      </td>
                                      <td>
                                        <Link
                                          to={`/drivers/driver-details/${item?._id}`}
                                          className="text-gray-800 text-hover-primary"
                                        >
                                          {item?.name}
                                        </Link>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            item?.sharing > 0 &&
                                            "badge bg-primary text-white"
                                          }
                                        >
                                          {item?.sharing > 0
                                            ? `${item?.sharing}%`
                                            : sharing > 0
                                              ? `${sharing}%`
                                              : 0}
                                        </span>
                                      </td>
                                      <td>{item?.lastOnline?.text ?? "---"}</td>
                                      <td>{item?.workPhone}</td>
                                      <td>
                                        <Status
                                          busy={item?.busy}
                                          online={item?.online}
                                          status={item?.status}
                                          orders={item?.orders}
                                        />
                                      </td>
                                      <PermissionGate
                                        required={`edit_driver_ring_priority`}
                                        fallback={<></>}
                                      >
                                        <td>
                                          {(() => {
                                            const category =
                                              item?.ringPriorityCategory ??
                                              (item?.excludeFromRingPriority === true
                                                ? "normal"
                                                : item?.delivaryCompanyId
                                                  ? "company_priority"
                                                  : "normal");

                                            if (category === "least_priority") {
                                              return (
                                                <span
                                                  className="badge badge-light-secondary fw-bold"
                                                  title="Least Priority driver"
                                                >
                                                  Least Priority
                                                </span>
                                              );
                                            }

                                            if (category === "company_priority") {
                                              return (
                                                <span
                                                  className="badge badge-light-success fw-bold"
                                                  title="Delivery company driver, included in priority ring"
                                                >
                                                  Company Priority
                                                </span>
                                              );
                                            }

                                            const isManuallyExcluded =
                                              item?.excludeFromRingPriority === true &&
                                              !!item?.delivaryCompanyId;

                                            return (
                                              <span
                                                className={
                                                  isManuallyExcluded
                                                    ? "badge badge-light-danger fw-bold"
                                                    : "badge badge-light-warning fw-bold"
                                                }
                                                title={
                                                  isManuallyExcluded
                                                    ? "Company driver excluded from priority ring"
                                                    : "Freelancer, normal ring priority"
                                                }
                                              >
                                                {isManuallyExcluded
                                                  ? "Excluded"
                                                  : "Freelancer"}
                                              </span>
                                            );
                                          })()}
                                        </td>
                                      </PermissionGate>
                                      <PermissionGate
                                        required={`edit_driver_ring_priority`}
                                        fallback={<></>}
                                      >
                                        <td>
                                          <span
                                            className={
                                              item?.leastRingPriority === true
                                                ? "badge badge-light-primary fw-bold"
                                                : "badge badge-light-secondary fw-bold"
                                            }
                                          >
                                            {item?.leastRingPriority === true
                                              ? "Yes"
                                              : "No"}
                                          </span>
                                        </td>
                                      </PermissionGate>
                                      <td className="">
                                        <div className="btn-group">
                                          {/*begin::View*/}
                                          <PermissionGate
                                            required={`view_driver_details`}
                                            fallback={<></>}
                                          >
                                            <Link
                                              to={`/drivers/driver-details/${item?._id}`}
                                              className="btn px-2 btn-outline btn-success btn-color-white btn-sm"
                                              title="Show Details"
                                            >
                                              <i className="bi-duotone bi-eye fs-5"></i>
                                            </Link>
                                          </PermissionGate>
                                          {/*end::View*/}
                                          {/*begin::Edit*/}
                                          <PermissionGate
                                            required={`edit_driver_details`}
                                            fallback={<></>}
                                            show={false}
                                          >
                                            <Link
                                              to={`/drivers/edit-driver/${item?._id}`}
                                              className="btn px-2 btn-outline btn-primary btn-color-white btn-sm"
                                              title="Edit"
                                            >
                                              <i className="bi-duotone bi-pen fs-5"></i>
                                            </Link>
                                          </PermissionGate>
                                          {/*end::Edit*/}
                                          {/*begin::Suspend*/}
                                          <Suspend
                                            openPopup={openPopup}
                                            item={item}
                                          />
                                          {/*end::Suspend*/}
                                          {/*begin::Online*/}
                                          <Online
                                            id={item?._id}
                                            openPopup={openPopup}
                                            busy={item?.busy}
                                          />
                                          {/*end::Online*/}
                                          {/*begin::Least Priority*/}
                                          <PermissionGate
                                            required={`edit_driver_ring_priority`}
                                            fallback={<></>}
                                          >
                                            <span
                                              onClick={() => {
                                                openPopup(
                                                  "change-least-priority",
                                                  item,
                                                );
                                              }}
                                              className={
                                                item?.leastRingPriority ===
                                                  true
                                                  ? "btn px-2 btn-outline btn-secondary btn-color-white btn-sm"
                                                  : "btn px-2 btn-outline btn-dark btn-color-white btn-sm"
                                              }
                                              title={
                                                item?.leastRingPriority ===
                                                  true
                                                  ? "Unmark Least Priority"
                                                  : "Mark As Least Priority"
                                              }
                                            >
                                              <i className="bi-duotone bi-arrow-down-circle fs-5"></i>
                                            </span>
                                          </PermissionGate>
                                          {/*end::Least Priority*/}
                                          {/*begin::Reset Password*/}
                                          <PermissionGate
                                            required={`reset_driver_password`}
                                            fallback={<></>}
                                          >
                                            <span
                                              onClick={() => {
                                                openPopup(
                                                  "reset-password",
                                                  item,
                                                );
                                              }}
                                              className="btn px-2 btn-outline btn-info btn-color-white btn-sm"
                                              title="Reset Password"
                                            >
                                              <i className="bi-duotone bi-key fs-5"></i>
                                            </span>
                                          </PermissionGate>
                                          {/*end::Reset Password*/}

                                          {/*begin::Change Password*/}
                                          <PermissionGate
                                            required={`change_driver_password`}
                                            fallback={<></>}
                                          >
                                            <span
                                              onClick={() => {
                                                openPopup(
                                                  "change-password",
                                                  item,
                                                );
                                              }}
                                              className="btn px-2 btn-outline btn-warning btn-color-white btn-sm"
                                              title="Change Password"
                                            >
                                              <i className="bi-duotone bi-shield-lock-fill fs-5"></i>
                                            </span>
                                          </PermissionGate>
                                          {/*end::Change Password*/}

                                          {/*begin::Delete*/}
                                          <PermissionGate
                                            required={`delete_driver`}
                                            fallback={<></>}
                                          >
                                            <span
                                              onClick={() => {
                                                openPopup("delete", {
                                                  id: item._id,
                                                });
                                              }}
                                              className="btn px-3 btn-outline btn-danger btn-color-white btn-sm"
                                              title="Delete Driver"
                                            >
                                              <i className="bi-duotone bi-trash fs-5"></i>
                                            </span>
                                          </PermissionGate>

                                          {/*end::Delete*/}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/*end::Table*/}
                      {/*begin::Paginate*/}
                      {count && Math.ceil(count / limit) > 1 ? (
                        <Pagination
                          page={page}
                          setPage={setPage}
                          count={count}
                          limit={limit}
                        />
                      ) : (
                        ""
                      )}
                      {/*begin::Paginate*/}
                    </div>
                    {/*end::Card body*/}
                  </div>
                </PermissionGate>
                {/*end::Card*/}
              </div>
              {/*end::Post*/}
            </div>
          </div>
          {/* start :: Change Status */}
          <div
            className={
              popup === "change-status"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
            id="kt_modal_1"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    CHANGE STATUS
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={changeStatus}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Change Status */}
          {/* start :: Change Least Priority */}
          <div
            className={
              popup === "change-least-priority"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    {driver?.leastRingPriority === true
                      ? "UNMARK LEAST PRIORITY"
                      : "MARK AS LEAST PRIORITY"}
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">
                    {driver?.leastRingPriority === true
                      ? "This driver will ring in their normal turn again."
                      : "This driver will ring dead last, after every other driver."}{" "}
                    Are You Sure ?
                  </p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={changeDriverLeastPriority}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Change Least Priority */}
          {/* start :: Change Availability */}
          <div
            className={
              popup === "change-availability"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    CHANGE AVAILABILITY
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={changeAvailability}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Change Availability */}
          {/* start :: Reset Password */}
          <div
            className={
              popup === "reset-password"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
            id="kt_modal_1"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-info text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    RESET PASSWORD
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={resetPassword}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: Reset Password */}
          {/*begin::Modal - Change Password*/}
          {popup && popup === "change-password" ? (
            <div
              className="modal fade show d-block"
              id="changePasswordModal"
              tabIndex="-1"
              aria-hidden="true"
            >
              {/*begin::Modal dialog*/}
              <div className="modal-dialog modal-dialog-centered modal-lg">
                {/*begin::Modal content*/}
                <div className="modal-content rounded">
                  {/*begin::Modal header*/}
                  <div className="modal-header">
                    {/*begin::Close*/}
                    <h4>Change Password</h4>
                    {/*end::Close*/}
                  </div>
                  {/*end::Modal header*/}

                  {/*begin::Modal body*/}
                  <div className="modal-body p-4">
                    <div className="row">
                      <div className="col">
                        <label className="form-label required">
                          New Password
                        </label>
                        <input
                          value={driver?.password ?? ""}
                          onChange={(e) => {
                            setDriver({
                              ...driver,
                              password: e.target.value,
                            });
                            setError({ ...error, password: "" });
                          }}
                          id="driver_password"
                          className="form-control"
                          placeholder="New Password"
                          autoComplete="off"
                          type="password"
                        />
                        {error.password !== undefined &&
                          error.password !== "" ? (
                          <small className="text-danger mt-2">
                            {error.password}
                          </small>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="col">
                        <label className="form-label required">
                          Confirm Password
                        </label>
                        <input
                          value={driver?.confirmPassword ?? ""}
                          onChange={(e) => {
                            setDriver({
                              ...driver,
                              confirmPassword: e.target.value,
                            });
                            setError({
                              ...error,
                              confirmPassword: "",
                            });
                          }}
                          id="driver_confirm_password"
                          className="form-control"
                          placeholder="Confirm Password"
                          autoComplete="off"
                          type="password"
                        />
                        {error.confirmPassword !== undefined &&
                          error.confirmPassword !== "" ? (
                          <small className="text-danger mt-2">
                            {error.confirmPassword}
                          </small>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>

                    {/*begin::Actions*/}
                    <div className="d-flex flex-row-fluid pt-12">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary me-3"
                        onClick={() => {
                          setPopup("");
                          setDriver({});
                          setError({});
                        }}
                      >
                        Close
                      </button>

                      <button
                        onClick={changePassword}
                        type="button"
                        className="btn btn-primary btn-sm"
                      >
                        Update
                      </button>
                    </div>
                    {/*end::Actions*/}
                  </div>
                  {/*end::Modal body*/}
                </div>
                {/*end::Modal content*/}
              </div>
              {/*end::Modal dialog*/}
            </div>
          ) : (
            ""
          )}
          {/*end::Modal - Change Employee Password*/}
          {/* start :: delete */}
          <div
            className={
              popup === "delete" ? "modal fade show d-block" : "modal fade"
            }
            tabIndex={-1}
            id="kt_modal_1"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-danger text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">DELETE</h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">Are You Sure ?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setDriver({});
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={deleteDriver}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: delete */}
          {/* start :: batch exclude ring priority */}
          <div
            className={
              popup === "batch-exclude-ring-priority"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    EXCLUDE FROM RING PRIORITY
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">
                    The {selectedDrivers.length} selected driver
                    {selectedDrivers.length === 1 ? "" : "s"} will no longer
                    get ring priority. Are You Sure ?
                  </p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => {
                      changeDriverRingPriorityBatch(true);
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: batch exclude ring priority */}
          {/* start :: batch include ring priority */}
          <div
            className={
              popup === "batch-include-ring-priority"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    INCLUDE IN RING PRIORITY
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">
                    The {selectedDrivers.length} selected driver
                    {selectedDrivers.length === 1 ? "" : "s"} will be eligible
                    for ring priority again. Are You Sure ?
                  </p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => {
                      changeDriverRingPriorityBatch(false);
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: batch include ring priority */}
          {/* start :: batch mark least priority */}
          <div
            className={
              popup === "batch-mark-least-priority"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    MARK AS LEAST PRIORITY
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">
                    The {selectedDrivers.length} selected driver
                    {selectedDrivers.length === 1 ? "" : "s"} will ring dead
                    last, after every other driver. Are You Sure ?
                  </p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => {
                      changeDriverLeastRingPriorityBatch(true);
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: batch mark least priority */}
          {/* start :: batch unmark least priority */}
          <div
            className={
              popup === "batch-unmark-least-priority"
                ? "modal fade show d-block"
                : "modal fade"
            }
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content text-center bg-warning text-light">
                <div className="modal-header">
                  <h5 className="modal-title mx-auto text-light">
                    UNMARK LEAST PRIORITY
                  </h5>
                </div>
                <div className="modal-body">
                  <p className="h4 text-light">
                    The {selectedDrivers.length} selected driver
                    {selectedDrivers.length === 1 ? "" : "s"} will ring in
                    their normal turn again. Are You Sure ?
                  </p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    onClick={() => {
                      setPopup("");
                    }}
                    type="button"
                    className="btn btn-light btn-sm"
                    data-bs-dismiss="modal"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => {
                      changeDriverLeastRingPriorityBatch(false);
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* end :: batch unmark least priority */}
          <Footer />
        </div>
        {/*end::Wrapper*/}
      </div>
      {/*end::Page*/}
    </div>
  );
};

export default AllDrivers;